import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useParams } from 'react-router-dom';
import Header from '../../components/Header/Header';
import Footer from '../../components/Footer/Footer';
import ShareRow from '../../components/ShareRow/ShareRow';
import { getSharedChart, ownsShare, stopSharing } from '../../services/chartService';
import { wrapReading } from '../BirthChart/ReadingText';
import {
  SynastryConnections, SynastryDetails, SynastryPeople, SynastryScore, personName,
} from './SynastryView';
import '../BirthChart/BirthChart.css';
import './Synastry.css';

const SharedSynastry = () => {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [owner, setOwner] = useState(false);
  const [stopped, setStopped] = useState(false);

  useEffect(() => {
    if (navigator.userAgent === 'ReactSnap') return undefined;
    let live = true;
    setOwner(ownsShare(id));
    getSharedChart(id)
      .then((result) => { if (live) setData(result); })
      .catch((err) => {
        if (!live) return;
        setError(err.status === 404 ? 'This reading is no longer shared.' : 'This reading could not be loaded. Try again in a moment.');
      });
    return () => { live = false; };
  }, [id]);

  const stop = async () => {
    try {
      await stopSharing(id);
      setStopped(true);
    } catch (err) {
      setError(err.message || 'Sharing could not be stopped.');
    }
  };

  const chart = data?.kind === 'synastry' ? data.chart : null;
  const youLabel = personName(chart?.people?.you, 'First person');
  const themLabel = personName(chart?.people?.them, 'Second person');
  const heading = chart ? `${youLabel} and ${themLabel}` : 'A shared compatibility reading';
  const shareUrl = `/birth-chart-compatibility/shared/${id}/`;

  return (
    <div className="birth-page">
      <Helmet>
        <title>{chart ? `${heading}: birth chart compatibility | NowTarot` : 'Shared compatibility reading | NowTarot'}</title>
        <meta name="robots" content="noindex" />
      </Helmet>
      <Header />
      <main className="birth-wide">
        <div className="birth-tool">
          {stopped ? (
            <header className="birth-intro">
              <p className="birth-eyebrow">Shared compatibility</p>
              <h1>You stopped sharing this reading.</h1>
              <p className="birth-lede">The link no longer works for anyone.</p>
            </header>
          ) : error || (data && !chart) ? (
            <header className="birth-intro">
              <p className="birth-eyebrow">Shared compatibility</p>
              <h1>{error || 'This link is not a compatibility reading.'}</h1>
              <p className="birth-lede">The person who shared it may have taken it down. You can compare your own charts below.</p>
            </header>
          ) : !chart ? (
            <p className="birth-wait">Loading the reading…</p>
          ) : (
            <div className="birth-result">
              <header className="birth-intro shared-intro">
                <p className="birth-eyebrow">Shared birth chart compatibility</p>
                <h1>{heading}</h1>
                {owner && (
                  <p className="birth-note shared-owner">
                    You shared this reading.{' '}
                    <button type="button" className="birth-text" onClick={stop}>Stop sharing</button>
                  </p>
                )}
              </header>
              <section className="birth-section">
                <SynastryScore score={chart.score} />
                <SynastryPeople people={chart.people} youLabel={youLabel} themLabel={themLabel} />
              </section>
              <section className="birth-section" aria-labelledby="shared-syn-reading">
                <h2 id="shared-syn-reading">The charts and reading</h2>
                <div className="birth-read">
                  <div className="birth-figure syn-figure">
                    <div className="birth-wheel" dangerouslySetInnerHTML={{ __html: chart.svg }} />
                    <SynastryDetails people={chart.people} overlays={chart.overlays} youLabel={youLabel} themLabel={themLabel} />
                  </div>
                  {wrapReading(data.reading, data.language)}
                </div>
              </section>
              <section className="birth-section" aria-labelledby="shared-syn-connections">
                <h2 id="shared-syn-connections">The connections between them</h2>
                <SynastryConnections connections={chart.connections} youLabel={youLabel} themLabel={themLabel} />
              </section>
              {data.qa?.length > 0 && (
                <section className="birth-section birth-ask" aria-labelledby="shared-syn-qa">
                  <h3 id="shared-syn-qa">Questions they asked</h3>
                  {data.qa.map((item, index) => (
                    <div className="birth-qa" key={index}>
                      <p className="birth-qa-question">{item.question}</p>
                      {wrapReading(item.answer, data.language)}
                    </div>
                  ))}
                </section>
              )}
              <section className="birth-section">
                <h2>Pass it on</h2>
                <ShareRow url={shareUrl} text={`${heading}, birth chart compatibility on NowTarot:`} label="" compact />
              </section>
            </div>
          )}
        </div>

        <section className="birth-section birth-compare shared-cta">
          <h2>Compare your own charts</h2>
          <p>Free, with the full reading. You only need two birthdays.</p>
          <Link className="birth-draw" to="/birth-chart-compatibility/">Compare two charts</Link>
          <p className="birth-note">
            <Link to="/birth-chart/">Draw your birth chart</Link>
            {' · '}
            <Link to="/zodiac-compatibility/">Zodiac sign compatibility</Link>
          </p>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default SharedSynastry;
